'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { DresscodeHorizontalLogo } from '@/components/Logo/DresscodeHorizontalLogo';
import { Text } from '@/components/Typography/Text';

export const Route = createFileRoute(
  '/_idp-sso-mock/idp-sso/google-workspace/',
)({
  component: GoogleWorkspaceSsoLaunch,
});

/** IdP 応答画面（ハンドシェイク）の表示時間（ミリ秒） */
const HANDSHAKE_DURATION_MS = 5000;
/** Google 側の署名検証画面（真っ白）の表示時間（ミリ秒） */
const VERIFY_DURATION_MS = 10;

const SERVICE_NAME = 'Google Workspace';

/** SAML SSO 成功後に到達する実際の Google の URL */
const GOOGLE_REDIRECT_URL = 'https://www.google.com/';

type Phase = 'handshake' | 'verifying';

/**
 * Cockpit のプロフィールドロップダウンにある Single Sign On の Google Workspace タイルから
 * 起動される、IdP-initiated SSO の利用フロー。
 *
 * handshake（DRESS CODE IdP が SAML Assertion を送出中）→
 * verifying（Google 側で SAML 署名を検証中。意図的に真っ白）→
 * 実際の Google（www.google.com）へブラウザごとリダイレクトする。
 * すべてフロントエンド内で完結し、外部サービスへの通信は行わない。
 */
function GoogleWorkspaceSsoLaunch() {
  const [phase, setPhase] = useState<Phase>('handshake');

  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase('verifying');
    }, HANDSHAKE_DURATION_MS);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (phase !== 'verifying') return;
    const timer = setTimeout(() => {
      window.location.href = GOOGLE_REDIRECT_URL;
    }, VERIFY_DURATION_MS);

    return () => clearTimeout(timer);
  }, [phase]);

  return (
    <VStack className="relative h-full">
      <TopLoadingBar />
      <div className="min-h-0 flex-1 overflow-auto">
        {phase === 'handshake' ? (
          <HandshakeScreen />
        ) : (
          // Google 側で署名検証中の一瞬を表す真っ白画面
          <div className="h-full w-full bg-white" />
        )}
      </div>
    </VStack>
  );
}

/**
 * 画面最上部の細いローディングバー。マウント中のみ描画し、初期値からタイマーで
 * 伸ばして CSS の width トランジションで読み込み中であることを表現する。
 */
function TopLoadingBar() {
  const [progress, setProgress] = useState(8);

  useEffect(() => {
    const timer = setTimeout(() => setProgress(92), 60);
    return () => clearTimeout(timer);
  }, []);

  return (
    <span
      className="absolute left-0 top-0 z-50 h-0.5 bg-primary-on-neutral-fill transition-[width] duration-[4800ms] ease-out"
      style={{ width: `${progress}%` }}
    />
  );
}

/**
 * DRESS CODE が IdP として SAML Assertion を生成し、SP（Google）へ送出している最中の遷移画面。
 * ユーザーは既に DRESS CODE で認証済みのため、外部サービスのサインイン画面を介さない。
 */
function HandshakeScreen() {
  return (
    <VStack className="h-full w-full items-center justify-center gap-4 bg-neutral-fill px-6 py-10 text-center">
      <DresscodeHorizontalLogo
        height={20}
        className="fill-primary-on-neutral-fill"
      />

      {/* SP <-> IdP 連携ビジュアル */}
      <HStack className="mt-2 items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-primary-on-neutral-fill">
          <Icon
            iconName="ShieldCheckIcon"
            colorVariant="white"
            className="h-5 w-5"
          />
        </span>
        <span className="h-0.5 w-9 animate-pulse rounded-full bg-primary-fill" />
        <span className="grid h-11 w-11 place-items-center rounded-full border border-neutral-muted-border bg-neutral-fill">
          <Icon iconName="GoogleWorkspaceIcon" className="h-5 w-5" />
        </span>
      </HStack>

      <Text variant="labelLarge" className="mt-1 text-neutral-on-fill">
        {SERVICE_NAME} にサインイン中
      </Text>

      <span className="my-1 h-6 w-6 animate-spin rounded-full border-2 border-neutral-muted-fill border-t-primary-on-neutral-fill" />

      <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
        そのままお待ちください。
        <br />
        自動的に {SERVICE_NAME} にリダイレクトされます。
      </Text>
    </VStack>
  );
}
